import { Routes } from '@angular/router';
import { MainLayout } from './components/main-layout/main-layout';
import { authGuard } from './core/guards/auth.guard';
import { roleGuard, denyRoleGuard } from './core/guards/role.guard';

// Surveyors only handle Consultations (their booked site visits) and Surveys
// (uploading reports) — everything else in the admin panel is sales/ops
// concerns that aren't theirs to touch.
const surveyorRestricted = { canActivate: [denyRoleGuard(['SURVEYOR'], '/admin/surveys')] };

export const routes: Routes = [
  // Public homeowner routes
  {
    path: '',
    component: MainLayout,
    children: [
      { path: '', redirectTo: '', pathMatch: 'full' },
      {
        path: '',
        data: { hideChrome: true },
        loadComponent: () => import('./components/landing-v3/landing-v3').then(m => m.LandingV3),
      },
      {
        path: 'landing-v2',
        data: { hideChrome: true },
        loadComponent: () => import('./components/landing-v2/landing-v2').then(m => m.LandingV2),
      },
      // {
      //   path: 'landing-v3',
      //   data: { hideChrome: true },
      //   loadComponent: () => import('./components/landing-v3/landing-v3').then(m => m.LandingV3),
      // },

      {
        path: 'contact',
        loadComponent: () =>
          import('./components/contact-kore/contact-kore').then(m => m.ContactKore),
      },
      // 'talk-to-expert' route disabled — the page didn't match the real Figma
      // "Get in Touch" design (contact-us frame). Rebuilding it against that frame
      // before re-enabling. See components/talk-to-expert/.
      // {
      //   path: 'talk-to-expert',
      //   loadComponent: () =>
      //     import('./components/talk-to-expert/talk-to-expert').then(m => m.TalkToExpert),
      // },
      {
        path: 'book-consultation',
        loadComponent: () =>
          import('./components/book-consultation/book-consultation').then(m => m.BookConsultation),
      },
      {
        path: 'adu-journey',
        loadComponent: () =>
          import('./components/adu-journey/adu-journey').then(m => m.AduJourney),
      },
      // Homeowner-facing link sent in the LOI email
      {
        path: 'loi/:loiId',
        loadComponent: () =>
          import('./components/loi-review/loi-review').then(m => m.LoiReview),
      },
      // Homeowner-facing link sent in the survey report email
      // {
      //   path: 'survey-report/:surveyId',
      //   loadComponent: () =>
      //     import('./components/survey-report/survey-report').then(m => m.SurveyReport),
      // },
      // Homeowner-facing link sent in the contract email
      {
        path: 'contract-review/:contractId',
        loadComponent: () =>
          import('./components/contract-review/contract-review').then(m => m.ContractReview),
      },
     
    ],
  },

  // Admin login (no guard)
  {
    path: 'admin/login',
    loadComponent: () =>
      import('./features/admin/auth/admin-login.component').then(m => m.AdminLoginComponent),
  },

  // Admin shell (guarded)
  {
    path: 'admin',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/admin/layout/admin-layout.component').then(m => m.AdminLayoutComponent),
    children: [
      { path: '', redirectTo: 'leads', pathMatch: 'full' },
      {
        path: 'leads',
        ...surveyorRestricted,
        data: { title: 'Leads' },
        loadComponent: () =>
          import('./features/admin/leads/leads-list/leads-list.component').then(m => m.LeadsListComponent),
      },
      {
        path: 'leads/:leadId',
        ...surveyorRestricted,
        data: { title: 'Lead Details', showBackButton: true },
        loadComponent: () =>
          import('./features/admin/leads/lead-detail/lead-detail.component').then(m => m.LeadDetailComponent),
      },
      {
        path: 'dashboard',
        ...surveyorRestricted,
        data: { title: 'Dashboard' },
        loadComponent: () =>
          import('./features/admin/dashboard/dashboard.component').then(m => m.DashboardComponent),
      },
      {
        path: 'executive-dashboard',
        canActivate: [roleGuard(['SUPER_ADMIN'])],
        data: { title: 'Executive Dashboard' },
        loadComponent: () =>
          import('./features/admin/executive-dashboard/executive-dashboard.component').then(m => m.ExecutiveDashboardComponent),
      },
      {
        path: 'prospects',
        ...surveyorRestricted,
        data: { title: 'Prospects' },
        loadComponent: () =>
          import('./features/admin/placeholder/admin-placeholder.component').then(m => m.AdminPlaceholderComponent),
      },
      {
        path: 'customers',
        ...surveyorRestricted,
        data: { title: 'Customers' },
        loadComponent: () =>
          import('./features/admin/placeholder/admin-placeholder.component').then(m => m.AdminPlaceholderComponent),
      },
      {
        path: 'feasibility',
        ...surveyorRestricted,
        data: { title: 'Initial Feasibility' },
        loadComponent: () =>
          import('./features/admin/placeholder/admin-placeholder.component').then(m => m.AdminPlaceholderComponent),
      },
      {
        path: 'consultations',
        data: { title: 'Consultations' },
        loadComponent: () =>
          import('./features/admin/consultations/consultations-list.component').then(m => m.ConsultationsListComponent),
      },
      {
        path: 'consultations/:consultationId',
        data: { title: 'Consultation Details', showBackButton: true },
        loadComponent: () =>
          import('./features/admin/consultations/consultation-detail.component').then(m => m.ConsultationDetailComponent),
      },
      {
        path: 'letters',
        ...surveyorRestricted,
        data: { title: 'Letters of Intent' },
        loadComponent: () =>
          import('./features/admin/letters/letters-list.component').then(m => m.LettersListComponent),
      },
      {
        path: 'letters/:loiId',
        ...surveyorRestricted,
        data: { title: 'Letter of Intent', showBackButton: true },
        loadComponent: () =>
          import('./features/admin/letters/letter-detail.component').then(m => m.LetterDetailComponent),
      },
      {
        path: 'surveys',
        data: { title: 'Surveys' },
        loadComponent: () =>
          import('./features/admin/surveys/surveys-list.component').then(m => m.SurveysListComponent),
      },
      {
        path: 'surveys/:surveyId',
        data: { title: 'On-Site Survey Report', showBackButton: true },
        loadComponent: () =>
          import('./features/admin/surveys/survey-detail.component').then(m => m.SurveyDetailComponent),
      },
      {
        path: 'contracts',
        ...surveyorRestricted,
        data: { title: 'Contracts' },
        loadComponent: () =>
          import('./features/admin/contracts/contracts-list.component').then(m => m.ContractsListComponent),
      },
      {
        path: 'contracts/:contractId',
        ...surveyorRestricted,
        data: { title: 'Contract', showBackButton: true },
        loadComponent: () =>
          import('./features/admin/contracts/contract-detail.component').then(m => m.ContractDetailComponent),
      },
      {
        path: 'reports',
        ...surveyorRestricted,
        data: { title: 'Reports' },
        loadComponent: () =>
          import('./features/admin/placeholder/admin-placeholder.component').then(m => m.AdminPlaceholderComponent),
      },
    ],
  },

  { path: '**', redirectTo: '' },
];
